import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import SearchIcon from '@mui/icons-material/Search';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import dayjs from 'dayjs';

const activityTypeConfig = {
  Calls: { icon: <PhoneInTalkOutlinedIcon fontSize="small" />, color: '#0ea5e9', bg: '#e0f2fe' },
  Emails: { icon: <EmailOutlinedIcon fontSize="small" />, color: '#2563eb', bg: '#eff6ff' },
  WhatsApp: { icon: <WhatsAppIcon fontSize="small" />, color: '#16a34a', bg: '#ecfdf3' },
  Meetings: { icon: <GroupsOutlinedIcon fontSize="small" />, color: '#7c3aed', bg: '#f3e8ff' },
  Notes: { icon: <StickyNote2OutlinedIcon fontSize="small" />, color: '#f59e0b', bg: '#fffbeb' },
};

const sampleActivities = [
  {
    id: 'ACT-001',
    type: 'Calls',
    title: 'Discovery follow-up call',
    description: 'Discussed integration scope and timeline alignment with procurement.',
    user: 'Ava Wilson',
    relatedRecord: 'Acme Corporation',
    dateTime: '2026-06-09T17:20:00',
  },
  {
    id: 'ACT-002',
    type: 'Emails',
    title: 'Proposal revision shared',
    description: 'Sent updated pricing sheet and executive summary deck to client.',
    user: 'Noah Bennett',
    relatedRecord: 'BrightPath Logistics',
    dateTime: '2026-06-09T15:05:00',
  },
  {
    id: 'ACT-003',
    type: 'WhatsApp',
    title: 'Stakeholder quick update',
    description: 'Shared deployment checklist and confirmed workshop attendance.',
    user: 'Emma Reed',
    relatedRecord: 'Nova Health Systems',
    dateTime: '2026-06-08T19:40:00',
  },
  {
    id: 'ACT-004',
    type: 'Meetings',
    title: 'Weekly governance meeting',
    description: 'Reviewed blockers, approved next sprint activities, and assigned owners.',
    user: 'Liam Carter',
    relatedRecord: 'GreenCore Energy',
    dateTime: '2026-06-08T11:00:00',
  },
  {
    id: 'ACT-005',
    type: 'Notes',
    title: 'Internal account insight',
    description: 'Customer prefers quarterly billing and wants legal review by month-end.',
    user: 'Olivia Brooks',
    relatedRecord: 'Enterprise Solutions Group',
    dateTime: '2026-06-07T16:30:00',
  },
  {
    id: 'ACT-006',
    type: 'Calls',
    title: 'Contract clarification call',
    description: 'Clarified service-level clause and support escalation matrix.',
    user: 'Ava Wilson',
    relatedRecord: 'Acme Corporation',
    dateTime: '2026-06-06T10:15:00',
  },
  {
    id: 'ACT-007',
    type: 'Emails',
    title: 'Meeting recap sent',
    description: 'Shared MoM and captured agreed deliverables for next review cycle.',
    user: 'Noah Bennett',
    relatedRecord: 'Nova Health Systems',
    dateTime: '2026-06-05T18:00:00',
  },
];

const activityTypes = ['all', 'Calls', 'Emails', 'WhatsApp', 'Meetings', 'Notes'];

const startOfDay = value => dayjs(value).startOf('day');
const endOfDay = value => dayjs(value).endOf('day');

export const Activities = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activities] = useState(sampleActivities);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityType, setActivityType] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [fromDate, setFromDate] = useState('2026-06-01');
  const [toDate, setToDate] = useState('2026-06-30');

  const users = useMemo(
    () => ['all', ...Array.from(new Set(activities.map(activity => activity.user))).sort((a, b) => a.localeCompare(b))],
    [activities]
  );

  const filteredActivities = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const from = startOfDay(fromDate);
    const to = endOfDay(toDate);

    return activities
      .filter(activity => {
        const activityAt = dayjs(activity.dateTime);

        const matchesSearch =
          !term ||
          [activity.title, activity.description, activity.relatedRecord, activity.user]
            .join(' ')
            .toLowerCase()
            .includes(term);

        const matchesType = activityType === 'all' || activity.type === activityType;
        const matchesUser = selectedUser === 'all' || activity.user === selectedUser;
        const matchesDateRange = activityAt.isAfter(from) && activityAt.isBefore(to);

        return matchesSearch && matchesType && matchesUser && matchesDateRange;
      })
      .sort((a, b) => dayjs(b.dateTime).valueOf() - dayjs(a.dateTime).valueOf());
  }, [activities, activityType, fromDate, searchTerm, selectedUser, toDate]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: '#f6f8fc', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <TimelineIcon sx={{ color: '#0288d1', fontSize: 30 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f3d5e' }}>
                CRM Activities
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Unified communication timeline across calls, messages, meetings, and notes
              </Typography>
            </Box>
          </Box>

          <Chip
            label={`${filteredActivities.length} item${filteredActivities.length === 1 ? '' : 's'}`}
            sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0c4a6e', border: '1px solid #bae6fd' }}
          />
        </Box>

        <Paper sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#f8fafc', mb: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr 1fr' },
              gap: 1,
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              placeholder="Search activity, description, record, user"
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              sx={{ bgcolor: '#fff' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ bgcolor: '#fff' }}>
              <InputLabel>Activity Type</InputLabel>
              <Select value={activityType} label="Activity Type" onChange={event => setActivityType(event.target.value)}>
                {activityTypes.map(type => (
                  <MenuItem key={type} value={type}>{type === 'all' ? 'All Types' : type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ bgcolor: '#fff' }}>
              <InputLabel>User</InputLabel>
              <Select value={selectedUser} label="User" onChange={event => setSelectedUser(event.target.value)}>
                {users.map(user => (
                  <MenuItem key={user} value={user}>{user === 'all' ? 'All Users' : user}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label="From"
              value={fromDate}
              onChange={event => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: '#fff' }}
            />

            <TextField
              size="small"
              type="date"
              label="To"
              value={toDate}
              onChange={event => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ bgcolor: '#fff' }}
            />
          </Box>
        </Paper>

        <Box sx={{ position: 'relative', pl: { xs: 0, sm: 1 } }}>
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                left: 20,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: '#dbeafe',
              }}
            />
          )}

          <Stack spacing={1.5}>
            {filteredActivities.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2.5, textAlign: 'center' }}>
                <Typography color="text.secondary">No activities found for the selected filters.</Typography>
              </Paper>
            ) : (
              filteredActivities.map(activity => {
                const config = activityTypeConfig[activity.type];

                return (
                  <Box key={activity.id} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', position: 'relative' }}>
                    {!isMobile && (
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: config.bg,
                          color: config.color,
                          border: `1px solid ${config.color}55`,
                          zIndex: 1,
                        }}
                      >
                        {config.icon}
                      </Avatar>
                    )}

                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        flex: 1,
                        borderColor: '#e2e8f0',
                        background: '#fff',
                        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.04)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.8 }}>
                        <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.8 }}>
                          <Typography sx={{ fontWeight: 800, color: '#1f2937' }}>{activity.title}</Typography>
                          <Chip
                            size="small"
                            label={activity.type}
                            sx={{
                              fontWeight: 700,
                              color: config.color,
                              bgcolor: config.bg,
                              border: `1px solid ${config.color}33`,
                            }}
                          />
                        </Stack>

                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                          {dayjs(activity.dateTime).format('DD MMM YYYY • hh:mm A')}
                        </Typography>
                      </Box>

                      <Typography variant="body2" sx={{ color: '#334155', mb: 1 }}>{activity.description}</Typography>

                      <Divider sx={{ my: 1 }} />

                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.8 }}>
                        <Chip
                          size="small"
                          icon={<PersonOutlineIcon />}
                          label={activity.user}
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Related: <strong>{activity.relatedRecord}</strong>
                        </Typography>
                      </Stack>
                    </Paper>
                  </Box>
                );
              })
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default Activities;
